/* Synthesised wind-and-stone ambience — Web Audio only, no assets. */
let ctx = null;
let master = null;
let nodes = [];
let listeners = new Set();
let playing = false;

const notify = () => listeners.forEach((fn) => fn(playing));
export const onAmbienceChange = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};
export const isAmbiencePlaying = () => playing;

function brownNoise(ac) {
  const len = ac.sampleRate * 4;
  const buf = ac.createBuffer(1, len, ac.sampleRate);
  const d = buf.getChannelData(0);
  let last = 0;
  for (let i = 0; i < len; i++) {
    const w = Math.random() * 2 - 1;
    last = (last + 0.02 * w) / 1.02;
    d[i] = last * 3.5;
  }
  const src = ac.createBufferSource();
  src.buffer = buf;
  src.loop = true;
  return src;
}

function build(ac) {
  const t = ac.currentTime;
  master = ac.createGain();
  master.gain.setValueAtTime(0.0001, t);
  master.gain.exponentialRampToValueAtTime(0.9, t + 2.5);
  master.connect(ac.destination);

  /* wind — brown noise → slowly wandering band-pass */
  const wind = brownNoise(ac);
  const bp = ac.createBiquadFilter();
  bp.type = "bandpass";
  bp.frequency.value = 320;
  bp.Q.value = 0.7;
  const windGain = ac.createGain();
  windGain.gain.value = 0.18;
  const gust = ac.createOscillator();
  gust.type = "sine";
  gust.frequency.value = 0.07;
  const gustAmt = ac.createGain();
  gustAmt.gain.value = 180;
  gust.connect(gustAmt).connect(bp.frequency);
  const swell = ac.createOscillator();
  swell.frequency.value = 0.045;
  const swellAmt = ac.createGain();
  swellAmt.gain.value = 0.08;
  swell.connect(swellAmt).connect(windGain.gain);
  wind.connect(bp).connect(windGain).connect(master);

  /* stone — detuned sub drones at 54 Hz / 108 Hz (432 ÷ 8, ÷ 4) */
  const droneGain = ac.createGain();
  droneGain.gain.value = 0.11;
  const lp = ac.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.value = 240;
  droneGain.connect(lp).connect(master);
  const oscs = [54, 54.4, 108, 107.6].map((f, i) => {
    const o = ac.createOscillator();
    o.type = i < 2 ? "sine" : "triangle";
    o.frequency.value = f;
    const g = ac.createGain();
    g.gain.value = i < 2 ? 0.6 : 0.22;
    o.connect(g).connect(droneGain);
    return o;
  });

  /* slow breathing on the drone */
  const breath = ac.createOscillator();
  breath.frequency.value = 0.03;
  const breathAmt = ac.createGain();
  breathAmt.gain.value = 0.05;
  breath.connect(breathAmt).connect(droneGain.gain);

  nodes = [wind, gust, swell, breath, ...oscs];
  nodes.forEach((n) => n.start());
}

export async function startAmbience() {
  if (playing) return;
  if (!ctx) ctx = new (window.AudioContext || window.webkitAudioContext)();
  if (ctx.state === "suspended") await ctx.resume();
  build(ctx);
  playing = true;
  localStorage.setItem("aetheria-ambience", "1");
  notify();
}

export function stopAmbience() {
  if (!playing || !ctx) return;
  const t = ctx.currentTime;
  const m = master;
  const old = nodes;
  m.gain.cancelScheduledValues(t);
  m.gain.setValueAtTime(m.gain.value, t);
  m.gain.exponentialRampToValueAtTime(0.0001, t + 1.2);
  setTimeout(() => {
    old.forEach((n) => {
      try {
        n.stop();
      } catch {
        /* already stopped */
      }
    });
    m.disconnect();
  }, 1400);
  nodes = [];
  master = null;
  playing = false;
  localStorage.setItem("aetheria-ambience", "0");
  notify();
}

export const toggleAmbience = () => (playing ? stopAmbience() : startAmbience());

/* Soft struck-stone chime for tour stops. */
export function chime() {
  if (!playing || !ctx) return;
  const t = ctx.currentTime;
  [432, 648, 864].forEach((f, i) => {
    const o = ctx.createOscillator();
    o.type = "sine";
    o.frequency.value = f;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.09 / (i + 1), t + 0.02 + i * 0.03);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 2.4 - i * 0.4);
    o.connect(g).connect(ctx.destination);
    o.start(t);
    o.stop(t + 2.6);
  });
}
